import KpiCardWidget, { KPI_METRIC_OPTIONS } from './KpiCardWidget';
import WeeklyChartWidget from './WeeklyChartWidget';
import CalendarWidget from './CalendarWidget';
import CurrentResultsTableWidget from './CurrentResultsTableWidget';
import { MyKpiWidget, MyEquityWidget, MyPnlCalendarWidget, MY_KPI_OPTIONS } from './myData/MyDataWidgets';
import { ALL_ACCOUNTS, MY_RANGE_OPTIONS, myAccountOptions } from './myData/myDataApi';

/*
 * Data source: the KPI, chart and calendar widgets show either a company
 * strategy or the viewer's own accounts (My Portfolio, Pro). Fields with
 * showWhen only appear, and are only required, for the matching source.
 * The shared company dashboard has no source choice (always the strategy).
 */
const SOURCE_MINE = 'mine';
const isMine = (c) => c?.source === SOURCE_MINE;
const notMine = (c) => !isMine(c);

const SOURCE_FIELD = {
  key: 'source',
  label: 'Data source',
  type: 'select',
  options: [
    { value: 'strategy', label: 'A strategy' },
    { value: SOURCE_MINE, label: 'My accounts' },
  ],
  defaultValue: 'strategy',
  noBlank: true,
};
const MY_ACCOUNT_FIELD = {
  key: 'account',
  label: 'Account',
  type: 'select',
  optionsProvider: () => myAccountOptions(),
  defaultValue: ALL_ACCOUNTS,
  noBlank: true,
  showWhen: isMine,
};
const MY_RANGE_FIELD = {
  key: 'range', label: 'Period', type: 'select', options: MY_RANGE_OPTIONS, noBlank: true, showWhen: isMine,
};
const MY_METRIC_FIELD = {
  key: 'myMetric', label: 'Metric', type: 'select', options: MY_KPI_OPTIONS, noBlank: true, showWhen: isMine,
};

/** Strategy-only fields get showWhen: not "My accounts". */
const forStrategy = (field) => ({ ...field, showWhen: notMine });

function withSource(fields, mineFields, ctx) {
  if (ctx?.companyDashboard) return fields;
  return [SOURCE_FIELD, ...fields.map(forStrategy), ...mineFields];
}

/** Render the "My accounts" version when the source is mine. */
function sourced(StrategyComponent, MineComponent, mapConfig) {
  function SourcedWidget(props) {
    if (isMine(props.config)) return <MineComponent {...props} config={mapConfig(props.config)} />;
    return <StrategyComponent {...props} />;
  }
  SourcedWidget.displayName = `Sourced(${StrategyComponent.displayName || StrategyComponent.name || 'Widget'})`;
  return SourcedWidget;
}

const mineDefaults = { account: ALL_ACCOUNTS, range: '3M', myMetric: 'netPnl' };

const STRATEGY_FIELD = (strategiesProvider) => ({
  key: 'strategy',
  label: 'Strategy',
  type: 'select',
  required: true,
  optionsProvider: strategiesProvider,
});

const CONTRACT_TYPE_FIELD = {
  key: 'contractType',
  label: 'Contract type',
  type: 'select',
  options: [
    { value: 'NQ', label: 'NQ' },
    { value: 'MNQ', label: 'MNQ' },
  ],
  defaultValue: 'NQ',
};

const CONTRACT_SIZE_FIELD = {
  key: 'contractSize',
  label: 'Default Contract Size Multiplier',
  type: 'number',
  min: 1,
  max: 100,
  defaultValue: 1,
};

const METRIC_FIELD = {
  key: 'metric',
  label: 'Metric',
  type: 'select',
  options: KPI_METRIC_OPTIONS.map((m) => ({ value: m.value, label: m.label })),
  defaultValue: 'netTotal',
};

const WIDGET_REGISTRY = {
  kpi: {
    label: 'KPI Card',
    description: 'A single number (Net Total, Win Rate, Best Day…) for a strategy or your own accounts.',
    component: sourced(KpiCardWidget, MyKpiWidget, (c) => ({ ...c, metric: c.myMetric || 'netPnl' })),
    /** Default width on the 12-column grid (~4 cards per row). */
    defaultGridSpan: 3,
    /** Allow fairly narrow KPIs; content still wraps inside the card. */
    minGridSpan: 2,
    defaultSize: { w: 3, h: 3 },
    minSize: { w: 2, h: 2 },
    configFields: (strategiesProvider, ctx) => withSource([
      STRATEGY_FIELD(strategiesProvider),
      METRIC_FIELD,
      CONTRACT_TYPE_FIELD,
      CONTRACT_SIZE_FIELD,
    ], [MY_METRIC_FIELD, MY_ACCOUNT_FIELD, MY_RANGE_FIELD], ctx),
    defaultConfig: () => ({
      source: 'strategy',
      ...mineDefaults,
      strategy: '',
      metric: 'netTotal',
      contractType: 'NQ',
      contractSize: 1,
    }),
  },
  weeklyChart: {
    label: 'Cumulative Chart',
    description: 'Cumulative P&L for a strategy, or your own equity curve.',
    component: sourced(WeeklyChartWidget, MyEquityWidget, (c) => c),
    defaultGridSpan: 6,
    defaultSize: { w: 6, h: 5 },
    minSize: { w: 3, h: 3 },
    configFields: (strategiesProvider, ctx) => withSource([
      STRATEGY_FIELD(strategiesProvider),
      CONTRACT_TYPE_FIELD,
      CONTRACT_SIZE_FIELD,
    ], [MY_ACCOUNT_FIELD, MY_RANGE_FIELD], ctx),
    defaultConfig: () => ({
      source: 'strategy',
      ...mineDefaults,
      strategy: '',
      contractType: 'NQ',
      contractSize: 1,
    }),
  },
  calendar: {
    label: 'Calendar',
    description: 'Monthly P&L calendar for a strategy or your own accounts.',
    component: sourced(CalendarWidget, MyPnlCalendarWidget, (c) => c),
    /**
     * Span two implicit grid rows so a following wide widget (e.g. cumulative chart)
     * can occupy columns beside the lower row—under small KPIs on the right—instead
     * of snapping to column 1 below the calendar only.
     */
    gridRowSpan: 2,
    defaultGridSpan: 6,
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 4, h: 4 },
    configFields: (strategiesProvider, ctx) => withSource([
      STRATEGY_FIELD(strategiesProvider),
      CONTRACT_TYPE_FIELD,
      CONTRACT_SIZE_FIELD,
    ], [MY_ACCOUNT_FIELD], ctx),
    defaultConfig: () => ({
      source: 'strategy',
      ...mineDefaults,
      strategy: '',
      contractType: 'NQ',
      contractSize: 1,
    }),
  },
  currentResultsTable: {
    label: 'Current Results Table',
    description: 'Live mirror of the Results "Current Results" sheet.',
    component: CurrentResultsTableWidget,
    defaultGridSpan: 12,
    defaultSize: { w: 8, h: 6 },
    minSize: { w: 4, h: 4 },
    configFields: () => [
      {
        key: 'note',
        label: 'Title note (optional)',
        type: 'text',
        maxLength: 60,
      },
    ],
    defaultConfig: () => ({ note: '' }),
  },
};

export const WIDGET_TYPES = Object.entries(WIDGET_REGISTRY).map(([type, meta]) => ({
  type,
  label: meta.label,
  description: meta.description,
}));

export function getWidgetMeta(type) {
  return WIDGET_REGISTRY[type] || null;
}

export default WIDGET_REGISTRY;

export { SOURCE_MINE, isMine as isMyAccountsSource };
